// Appels d'auth depuis le navigateur (remplace le client Supabase). Aucune
// dépendance serveur : importable par les composants "use client".

import { messageFor, NETWORK_ERROR } from "@shared/client/api";
import type { Locale } from "@shared/i18n/locales";
import type { UserMetadata } from "@shared/lib/auth-shared";

async function call<T = Record<string, unknown>>(path: string, method: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: "same-origin",
  }).catch(() => {
    // Coupure réseau : le message du navigateur (« Failed to fetch ») est anglais et muet.
    throw new Error(messageFor(NETWORK_ERROR));
  });
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  // Les libellés d'apiFetch quand la réponse n'en porte pas (page du relais, corps vide).
  if (!response.ok) throw new Error(payload.error || messageFor(response.status));
  return payload;
}

export const signIn = (email: string, password: string) =>
  call<{ onboardingCompleted: boolean }>("/api/auth/login", "POST", { email, password });

export const signUp = (email: string, password: string) => call("/api/auth/signup", "POST", { email, password });

export const signOut = () => call("/api/auth/logout", "POST");

export const requestPasswordReset = (email: string) => call("/api/auth/forgot", "POST", { email });

/** Avec `token` : lien reçu par e-mail. Sans : depuis une session ouverte. */
export const resetPassword = (password: string, token?: string | null) =>
  call("/api/auth/reset", "POST", { password, ...(token ? { token } : {}) });

export const updateProfile = (data: Partial<UserMetadata>) => call("/api/auth/user", "PATCH", { data });

/** The account's language (app, emails, what the agent writes). */
export const updateLocale = (locale: Locale) => call("/api/auth/user", "PATCH", { locale });

/** Démarre la connexion Google (redirection pleine page). */
export function signInWithGoogle(next: string) {
  window.location.assign(`/api/auth/google?next=${encodeURIComponent(next)}`);
}
